/**
 * ReactionManager - In-Game Emoji & Table Banter System
 * Allows players to send floating emojis and authentic cribbage table banter
 * with acoustic sound effects and speech synthesis during play.
 * Supports local play, multiplayer peer synchronization, and contextual AI reactions.
 */

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export const REACTION_ITEMS = [
  { id: 'skunked', emoji: '🦨', label: 'Skunked', sfxMethod: 'reactionSkunk' },
  { id: 'cheers', emoji: '🍻', label: 'Cheers', sfxMethod: 'reactionCheers' },
  { id: 'noway', emoji: '😱', label: 'No Way!', sfxMethod: 'reactionNoWay' },
  { id: 'nice15', emoji: '🃏', label: 'Nice 15', sfxMethod: 'reactionNice15' },
  { id: 'boom', emoji: '💥', label: 'Boom!', sfxMethod: 'reactionBoom' }
];

export const BANTER_ITEMS = [
  {
    id: 'fifteen24',
    emoji: '🔢',
    text: "Fifteen two, fifteen four, and the rest don't score!",
    short: "15-2, 15-4…",
    sfxMethod: 'banterFifteenTwo'
  },
  {
    id: 'nob',
    emoji: '🎩',
    text: "One for his nob!",
    short: "His Nob!",
    sfxMethod: 'banterNob'
  },
  {
    id: 'go',
    emoji: '🏃',
    text: "Go!",
    short: "Go!",
    sfxMethod: 'banterGo'
  },
  {
    id: 'pair2',
    emoji: '✌️',
    text: "Pair for two!",
    short: "Pair for 2!",
    sfxMethod: 'banterPair'
  },
  {
    id: 'thirtyone',
    emoji: '👑',
    text: "Thirty-one for two!",
    short: "31 for 2!",
    sfxMethod: 'banterThirtyOne'
  },
  {
    id: 'heels',
    emoji: '👢',
    text: "Two for his heels!",
    short: "His Heels!",
    sfxMethod: 'banterHeels'
  }
];

export class ReactionManager {
  static isWheelOpen = false;
  static activeTab = 'emotes'; // 'emotes' | 'banter'
  static _lastReactionTime = 0;

  /**
   * Returns definition for a reaction emoji by id.
   * @param {string} id
   * @returns {Object|null}
   */
  static getReaction(id) {
    return REACTION_ITEMS.find(r => r.id === id) || null;
  }

  /**
   * Returns definition for a banter phrase by id.
   * @param {string} id
   * @returns {Object|null}
   */
  static getBanter(id) {
    return BANTER_ITEMS.find(b => b.id === id) || null;
  }

  /**
   * Triggers an emoji reaction locally, plays acoustic SFX, spawns floating emoji on board,
   * and broadcasts to online peers if in a multiplayer room.
   * @param {string} reactionId
   * @param {Object} [options]
   */
  static triggerReaction(reactionId, options = {}) {
    const item = ReactionManager.getReaction(reactionId);
    if (!item) return;

    const now = Date.now();
    // Throttle spam (max 1 reaction per 400ms)
    if (now - ReactionManager._lastReactionTime < 400) return;
    ReactionManager._lastReactionTime = now;

    const sfx = options.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    if (sfx && typeof sfx[item.sfxMethod] === 'function') {
      sfx[item.sfxMethod]();
    }

    const senderName = options.senderName || (typeof window !== 'undefined' && typeof window.pName === 'function' ? window.pName('you') : 'You');
    const fromSeat = options.fromSeat || 'p1';

    // Spawn animated floating reaction element
    ReactionManager.displayFloatingReaction(item.emoji, item.label, {
      senderName,
      fromSeat,
      isLocal: true,
      isBanter: false
    });

    // Close the reaction wheel after selection
    ReactionManager.closeReactionWheel();

    // Broadcast over multiplayer network if in an online game
    const net = options.net || (typeof window !== 'undefined' ? window.net : null);
    const netSend = options.netSend || (typeof window !== 'undefined' ? window.netSend : null);
    if (net && net.room && typeof netSend === 'function') {
      netSend({
        type: 'reaction',
        id: item.id,
        emoji: item.emoji,
        label: item.label,
        senderName,
        fromSeat,
        isBanter: false
      });
    }
  }

  /**
   * Triggers a friendly cribbage table banter phrase locally, plays sound bite/speech,
   * spawns floating speech bubble on the table, and broadcasts to online peers.
   * @param {string} banterId
   * @param {Object} [options]
   */
  static triggerBanter(banterId, options = {}) {
    const item = ReactionManager.getBanter(banterId);
    if (!item) return;

    const now = Date.now();
    if (now - ReactionManager._lastReactionTime < 400) return;
    ReactionManager._lastReactionTime = now;

    const sfx = options.sfx || (typeof window !== 'undefined' ? window.sfx : null);
    if (sfx) {
      if (typeof sfx.playBanterSound === 'function') {
        sfx.playBanterSound(item.sfxMethod, item.text);
      } else if (typeof sfx[item.sfxMethod] === 'function') {
        sfx[item.sfxMethod]();
      }
    }

    const senderName = options.senderName || (typeof window !== 'undefined' && typeof window.pName === 'function' ? window.pName('you') : 'You');
    const fromSeat = options.fromSeat || 'p1';

    // Spawn animated floating speech bubble
    ReactionManager.displayFloatingReaction(item.emoji, item.text, {
      senderName,
      fromSeat,
      isLocal: true,
      isBanter: true
    });

    // Close the reaction wheel after selection
    ReactionManager.closeReactionWheel();

    // Broadcast over multiplayer network if in an online game
    const net = options.net || (typeof window !== 'undefined' ? window.net : null);
    const netSend = options.netSend || (typeof window !== 'undefined' ? window.netSend : null);
    if (net && net.room && typeof netSend === 'function') {
      netSend({
        type: 'reaction',
        id: item.id,
        emoji: item.emoji,
        label: item.text,
        senderName,
        fromSeat,
        isBanter: true
      });
    }

    // Trigger playful AI banter response if playing vs computer
    ReactionManager.respondAiToBanter(item.id, { sfx });
  }

  /**
   * Handles incoming reaction or banter message from remote peer.
   * @param {Object} msg
   * @param {Object} [options]
   */
  static handleRemoteReaction(msg, options = {}) {
    if (!msg || (!msg.emoji && !msg.label)) return;
    const isBanter = Boolean(msg.isBanter);
    const item = isBanter ? ReactionManager.getBanter(msg.id) : ReactionManager.getReaction(msg.id);
    const sfx = options.sfx || (typeof window !== 'undefined' ? window.sfx : null);

    if (sfx) {
      if (isBanter && item && typeof sfx.playBanterSound === 'function') {
        sfx.playBanterSound(item.sfxMethod, item.text);
      } else if (item && typeof sfx[item.sfxMethod] === 'function') {
        sfx[item.sfxMethod]();
      }
    }

    // Prefer verified item properties if registered, otherwise sanitize incoming values
    const safeEmoji = item ? item.emoji : (typeof msg.emoji === 'string' ? msg.emoji.replace(/[<>"'&`/\\;]/g, '').trim().slice(0, 10) : '💬');
    const safeLabel = item ? (isBanter ? item.text : item.label) : (typeof msg.label === 'string' ? msg.label.slice(0, 100) : '');
    const safeSender = typeof msg.senderName === 'string'
      ? msg.senderName.replace(/[<>"'&`/\\;]/g, '').trim().slice(0, 24) || 'Opponent'
      : 'Opponent';

    ReactionManager.displayFloatingReaction(safeEmoji, safeLabel, {
      senderName: safeSender,
      fromSeat: msg.fromSeat || 'p2',
      isLocal: false,
      isBanter
    });
  }

  /**
   * Contextual reaction triggered by computer AI.
   * @param {string} trigger Event trigger type ('nice15', 'boom', 'skunk', 'cheers')
   * @param {Object} [options]
   */
  static triggerAiReaction(trigger, options = {}) {
    const item = ReactionManager.getReaction(trigger);
    if (!item) return;

    const delay = options.delay != null ? options.delay : (400 + Math.random() * 500);
    setTimeout(() => {
      const sfx = options.sfx || (typeof window !== 'undefined' ? window.sfx : null);
      if (sfx && typeof sfx[item.sfxMethod] === 'function') {
        sfx[item.sfxMethod]();
      }
      const aiName = options.aiName || (typeof window !== 'undefined' && typeof window.pName === 'function' ? window.pName('cpu') : 'Computer');
      ReactionManager.displayFloatingReaction(item.emoji, item.label, {
        senderName: aiName,
        fromSeat: 'cpu',
        isLocal: false,
        isBanter: false
      });
    }, delay);
  }

  /**
   * AI contextual banter response to player table banter.
   * @param {string} playerBanterId
   * @param {Object} [options]
   */
  static respondAiToBanter(playerBanterId, options = {}) {
    const aiName = options.aiName || (typeof window !== 'undefined' && typeof window.pName === 'function' ? window.pName('cpu') : 'Computer');
    const delay = 650 + Math.random() * 700;

    let reply = null;
    let isReplyBanter = false;

    if (playerBanterId === 'go') {
      reply = { emoji: '👀', text: "Taking the point!" };
      isReplyBanter = true;
    } else if (playerBanterId === 'fifteen24') {
      if (Math.random() > 0.45) {
        reply = ReactionManager.getReaction('noway') || { emoji: '😱', label: 'No Way!' };
      } else {
        reply = { emoji: '🃏', text: "Count 'em up!" };
        isReplyBanter = true;
      }
    } else if (playerBanterId === 'nob' || playerBanterId === 'heels') {
      reply = Math.random() > 0.5 ? ReactionManager.getReaction('cheers') : { emoji: '🎩', text: "Well cut!" };
      isReplyBanter = !reply.sfxMethod;
    } else if (playerBanterId === 'pair2') {
      reply = { emoji: '⚔️', text: "Nice pair!" };
      isReplyBanter = true;
    }

    if (reply) {
      setTimeout(() => {
        const sfx = options.sfx || (typeof window !== 'undefined' ? window.sfx : null);
        if (sfx) {
          if (reply.sfxMethod && typeof sfx[reply.sfxMethod] === 'function') {
            sfx[reply.sfxMethod]();
          } else if (typeof sfx.thunk === 'function') {
            sfx.thunk(1.15);
          }
        }
        ReactionManager.displayFloatingReaction(reply.emoji, reply.text || reply.label || '', {
          senderName: aiName,
          fromSeat: 'cpu',
          isLocal: false,
          isBanter: isReplyBanter
        });
      }, delay);
    }
  }

  /**
   * Spawns an animated floating reaction or banter speech bubble that glides upward with organic bounce.
   * @param {string} emoji
   * @param {string} label
   * @param {Object} [options]
   */
  static displayFloatingReaction(emoji, label, options = {}) {
    if (typeof document === 'undefined') return;

    const targetContainer = document.getElementById('cribBoard') || document.getElementById('game') || document.body;
    if (!targetContainer) return;

    const isBanter = Boolean(options.isBanter);
    const el = document.createElement('div');
    el.className = 'floating-reaction' + (isBanter ? ' is-banter' : '') + (options.isLocal ? ' is-local' : ' is-remote');

    // Slight random horizontal offset for natural organic feel
    const randomOffset = (Math.random() - 0.5) * 55;
    el.style.setProperty('--react-offset-x', `${randomOffset}px`);

    const senderHtml = options.senderName ? `<span class="react-sender">${escapeHtml(options.senderName)}</span>` : '';

    if (isBanter) {
      el.innerHTML = `
        <div class="react-content is-banter">
          <div class="banter-bubble">
            <span class="banter-bubble-emoji">${escapeHtml(emoji)}</span>
            <span class="banter-bubble-quote">“${escapeHtml(label)}”</span>
          </div>
          ${senderHtml}
        </div>
      `;
    } else {
      el.innerHTML = `
        <div class="react-content">
          <span class="react-emoji">${escapeHtml(emoji)}</span>
          ${label ? `<span class="react-bubble">${escapeHtml(label)}</span>` : ''}
          ${senderHtml}
        </div>
      `;
    }

    targetContainer.appendChild(el);

    // Auto-remove upon animation conclusion
    const cleanup = () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
    el.addEventListener('animationend', cleanup, { once: true });
    setTimeout(cleanup, 2800); // Safety fallback timeout
  }

  /**
   * Switches between 'emotes' and 'banter' tabs in the dock.
   * @param {string} tab 'emotes' | 'banter'
   */
  static switchTab(tab) {
    if (typeof document === 'undefined') return;
    ReactionManager.activeTab = tab;
    const btnEmotes = document.getElementById('btnReactionTabEmotes');
    const btnBanter = document.getElementById('btnReactionTabBanter');
    const panelEmotes = document.getElementById('reactionPanelEmotes');
    const panelBanter = document.getElementById('reactionPanelBanter');

    if (btnEmotes && btnBanter && panelEmotes && panelBanter) {
      if (tab === 'banter') {
        btnEmotes.classList.remove('is-active');
        btnBanter.classList.add('is-active');
        panelEmotes.classList.add('hidden');
        panelBanter.classList.remove('hidden');
      } else {
        btnEmotes.classList.add('is-active');
        btnBanter.classList.remove('is-active');
        panelEmotes.classList.remove('hidden');
        panelBanter.classList.add('hidden');
      }
    }
  }

  /**
   * Toggles visibility of the in-game reaction wheel.
   */
  static toggleReactionWheel() {
    if (ReactionManager.isWheelOpen) {
      ReactionManager.closeReactionWheel();
    } else {
      ReactionManager.openReactionWheel();
    }
  }

  /**
   * Opens the in-game reaction wheel.
   */
  static openReactionWheel() {
    if (typeof document === 'undefined') return;
    const wheel = document.getElementById('reactionWheel');
    const trigger = document.getElementById('btnReactionTrigger');
    if (!wheel) return;

    wheel.classList.remove('hidden');
    wheel.classList.add('is-open');
    if (trigger) trigger.classList.add('is-active');
    ReactionManager.isWheelOpen = true;

    // Close on outside click
    const handleOutside = (e) => {
      if (!wheel.contains(e.target) && (!trigger || !trigger.contains(e.target))) {
        ReactionManager.closeReactionWheel();
        document.removeEventListener('click', handleOutside);
      }
    };
    setTimeout(() => {
      document.addEventListener('click', handleOutside);
    }, 10);
  }

  /**
   * Closes the in-game reaction wheel.
   */
  static closeReactionWheel() {
    if (typeof document === 'undefined') return;
    const wheel = document.getElementById('reactionWheel');
    const trigger = document.getElementById('btnReactionTrigger');
    if (wheel) {
      wheel.classList.remove('is-open');
      setTimeout(() => {
        if (!ReactionManager.isWheelOpen && wheel) wheel.classList.add('hidden');
      }, 160);
    }
    if (trigger) trigger.classList.remove('is-active');
    ReactionManager.isWheelOpen = false;
  }

  /**
   * Injects the reaction wheel and trigger button HTML markup into the play area if not already present.
   * @param {HTMLElement} [container]
   */
  static injectReactionUi(container) {
    if (typeof document === 'undefined') return;
    if (document.getElementById('reactionDockWrap')) return;

    const parent = container || document.getElementById('tableRight') || document.getElementById('game');
    if (!parent) return;

    const dock = document.createElement('div');
    dock.id = 'reactionDockWrap';
    dock.className = 'reaction-dock-wrap';

    const emotesHtml = REACTION_ITEMS.map((item, idx) => `
      <button type="button" class="reaction-item-btn" data-reaction="${item.id}" title="${item.label} (${item.emoji})" style="--item-index: ${idx}">
        <span class="reaction-item-emoji">${item.emoji}</span>
        <span class="reaction-item-label">${item.label}</span>
      </button>
    `).join('');

    const banterHtml = BANTER_ITEMS.map((item, idx) => `
      <button type="button" class="banter-item-btn" data-banter="${item.id}" title="${escapeHtml(item.text)}" style="--item-index: ${idx}">
        <span class="banter-item-emoji">${item.emoji}</span>
        <span class="banter-item-short">${escapeHtml(item.short)}</span>
      </button>
    `).join('');

    dock.innerHTML = `
      <div id="reactionWheel" class="reaction-wheel hidden" role="toolbar" aria-label="Table Banter & Emojis">
        <div class="reaction-wheel-tabs">
          <button type="button" class="reaction-tab-btn is-active" id="btnReactionTabEmotes">😀 Emotes</button>
          <button type="button" class="reaction-tab-btn" id="btnReactionTabBanter">💬 Banter</button>
        </div>
        <div class="reaction-panel" id="reactionPanelEmotes">
          ${emotesHtml}
        </div>
        <div class="reaction-panel hidden" id="reactionPanelBanter">
          ${banterHtml}
        </div>
      </div>
      <button type="button" id="btnReactionTrigger" class="reaction-trigger-btn" aria-label="Send game reaction emoji or banter" title="Table Banter & Reactions (💬)">
        <span class="reaction-trigger-icon">💬</span>
      </button>
    `;

    parent.appendChild(dock);

    // Bind tab switch buttons
    const tabEmotes = dock.querySelector('#btnReactionTabEmotes');
    if (tabEmotes) {
      tabEmotes.addEventListener('click', (e) => {
        e.stopPropagation();
        ReactionManager.switchTab('emotes');
      });
    }
    const tabBanter = dock.querySelector('#btnReactionTabBanter');
    if (tabBanter) {
      tabBanter.addEventListener('click', (e) => {
        e.stopPropagation();
        ReactionManager.switchTab('banter');
      });
    }

    // Bind trigger listener
    const trigger = dock.querySelector('#btnReactionTrigger');
    if (trigger) {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        ReactionManager.toggleReactionWheel();
      });
    }

    // Bind emote buttons
    const emoteBtns = dock.querySelectorAll('.reaction-item-btn');
    emoteBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const rId = btn.dataset.reaction;
        ReactionManager.triggerReaction(rId);
      });
    });

    // Bind banter buttons
    const banterBtns = dock.querySelectorAll('.banter-item-btn');
    banterBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const bId = btn.dataset.banter;
        ReactionManager.triggerBanter(bId);
      });
    });
  }
}
